import { logAmplitudeEvent } from 'amplitude/amplitude'
import get from 'lodash/get'
import omit from 'lodash/omit'
import type { Dispatch } from 'redux'

import { getBulkEditDefinition } from 'elements/listBulkEdit/bulkEditRegistry'
import { mergeBulkEditToolbarDefaults } from 'elements/listBulkEdit/helpers'
import type {
  BulkEditDialogOpenPayload,
  BulkEditListQuery,
  BulkEditResourceDefinition,
} from 'elements/listBulkEdit/types'
import { coerceBulkEditDialogOpenPayload } from 'elements/listBulkEdit/validators'
import type { RootState } from 'types/state'

export const LIST_BULK_EDIT_OPEN = 'LIST_BULK_EDIT_OPEN'
export const LIST_BULK_EDIT_CLOSE = 'LIST_BULK_EDIT_CLOSE'

export type ListBulkEditState = {
  session: BulkEditDialogOpenPayload | null
}

const defaultState: ListBulkEditState = {
  session: null,
}

type ListBulkEditOpenAction = { type: typeof LIST_BULK_EDIT_OPEN; payload: BulkEditDialogOpenPayload }
type ListBulkEditCloseAction = { type: typeof LIST_BULK_EDIT_CLOSE }

export type ListBulkEditAction = ListBulkEditOpenAction | ListBulkEditCloseAction

export function openListBulkEditSessionIfValid(
  dispatch: Dispatch<ListBulkEditAction>,
  full: BulkEditDialogOpenPayload,
  definition: BulkEditResourceDefinition,
  resource: string
): boolean {
  const payload = coerceBulkEditDialogOpenPayload(full, definition, resource)
  if (!payload) {
    return false
  }
  logAmplitudeEvent('list_bulk_edit_modal_opened', {
    resource: payload.resource,
    selection_mode: payload.selectedIds?.length ? 'explicit_ids' : 'list_query',
    explicit_selection_count: payload.selectedIds?.length ?? 0,
  })
  dispatch({ type: LIST_BULK_EDIT_OPEN, payload })
  return true
}

type ListParamsShape = {
  sort: string | null
  order: string | null
  page: number
  perPage: number | null
  filter: Record<string, unknown>
}

export type OpenListBulkEditFromListParamsExtras = Omit<BulkEditDialogOpenPayload, 'resource' | 'listQuery'> & {
  listQuery?: Partial<BulkEditListQuery>
}

export function openListBulkEditFromListParams(
  dispatch: Dispatch<ListBulkEditAction>,
  getState: () => RootState & { admin?: unknown },
  resource: string,
  extras?: OpenListBulkEditFromListParamsExtras
): void {
  const definition = getBulkEditDefinition(resource)
  if (!definition) {
    return
  }
  const state = getState()
  const params = get(state, ['admin', 'resources', resource, 'list', 'params']) as ListParamsShape | undefined
  const toolbarDefaults = mergeBulkEditToolbarDefaults(definition.bulkEditToolbarListDefaults)

  const sortField = (params?.sort ?? toolbarDefaults.sort.field) as string
  const orderRaw = (params?.order ?? toolbarDefaults.sort.order) as string
  const orderUpper = typeof orderRaw === 'string' ? orderRaw.toUpperCase() : 'ASC'

  const listQuery: BulkEditListQuery = {
    filter: params?.filter && typeof params.filter === 'object' && !Array.isArray(params.filter) ? params.filter : {},
    sort: { field: sortField, order: orderUpper === 'DESC' ? 'DESC' : 'ASC' },
    maxRecords: toolbarDefaults.maxRecords,
    ...(toolbarDefaults.perPage !== undefined ? { perPage: toolbarDefaults.perPage } : {}),
    ...(typeof params?.perPage === 'number' && params.perPage > 0 ? { perPage: params.perPage } : {}),
    ...extras?.listQuery,
  }

  const restExtras = omit(extras ?? {}, 'listQuery') as Omit<BulkEditDialogOpenPayload, 'resource' | 'listQuery'>
  const full: BulkEditDialogOpenPayload = {
    resource,
    listQuery,
    ...restExtras,
  }

  openListBulkEditSessionIfValid(dispatch, full, definition, resource)
}

export function closeListBulkEditSession(): ListBulkEditCloseAction {
  return { type: LIST_BULK_EDIT_CLOSE }
}

export default function listBulkEditReducer(
  previousState: ListBulkEditState = defaultState,
  action: ListBulkEditAction
): ListBulkEditState {
  switch (action.type) {
    case LIST_BULK_EDIT_OPEN:
      return { session: action.payload }
    case LIST_BULK_EDIT_CLOSE:
      return { session: null }
    default:
      return previousState
  }
}

export const listBulkEditSelectors = {
  getSession(state: RootState): BulkEditDialogOpenPayload | null {
    return state.listBulkEdit.session
  },
}
